/* eslint-disable no-undef */
import { toast } from 'react-toastify';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { cimode, Language, languages as supportedLanguages } from '../constants/Languages';
import { LocalStorage } from '../constants/LocalStorage';
import {
  Columns,
  defaultTableFilters,
  defaultVisibleColumns,
  TableFilterType,
  ToastMessageType,
} from '../constants/Viewer';
import {
  fetchGroupFilterSuccess,
  fetchVisibleColumnsError,
  fetchVisibleColumnsSuccess,
  setGroupFilterSuccess,
  setVisibleColumnsError,
  setVisibleColumnsSuccess,
  clearFiltersSuccess,
  ViewerActionTypes,
  setTheme,
  getThemeComplete,
  setVisibleLanguages,
} from '../ducks/viewer';
import { i18n } from '../i18n';
import { getGroupFilterParts } from '../selectors/getGroupFilters';
import { AppState } from '../types/AppState';
import { Configurator } from '../types/Configurator';
import { CurrentUserState } from '../types/CurrentUserState';
import { SelectedFilterValues, GroupFilter } from '../types/CustomFilter';
import { Group } from '../types/Group';
import { unknownUser } from '../types/User';
import { ViewerState } from '../types/ViewerState';
import { isIdeaRoomGroup, isIdeaRoomUser } from '../utils/userUtils';
import { extractThemeFromConfig } from '../utils/vendorDataUtils';

function* setToastMessage({ payload: { message, type } }: any): Generator {
  try {
    switch (type) {
      case ToastMessageType.Success:
        yield call(toast.success, message);
        break;
      case ToastMessageType.Error:
        yield call(toast.error, message);
        break;
      case ToastMessageType.Warning:
        yield call(toast.warn, message);
        break;
      case ToastMessageType.Info:
        yield call(toast.info, message);
        break;
      default:
        yield call(toast.info, message);
        break;
    }
  } catch (error) {
    console.log('Error setting toast message: ', error);
  }
}

function* fetchVisibleColumns(): Generator {
  try {
    const storedVisibleColumns = localStorage.getItem(LocalStorage.VisibleColumnKey);
    let visibleColumns = defaultVisibleColumns;

    const validColumns = storedVisibleColumns
      ? (storedVisibleColumns.split(',') as Columns[]).filter((column) => Object.values(Columns).includes(column))
      : [];
    if (validColumns && validColumns.length > 0) {
      visibleColumns = validColumns;
    }

    yield put(fetchVisibleColumnsSuccess(visibleColumns));
  } catch (error) {
    yield put(fetchVisibleColumnsError());
  }
}

function* setVisibleColumns({ payload: { visibleColumns } }: any): Generator {
  try {
    localStorage.setItem(LocalStorage.VisibleColumnKey, visibleColumns.join(','));

    yield put(setVisibleColumnsSuccess(visibleColumns));
  } catch (error) {
    yield put(setVisibleColumnsError());
  }
}

function* fetchGroupFilters(): Generator {
  try {
    const { groupId } = (yield select(({ currentUser: { group } }: AppState) => group)) as Group;
    const groupFiltersFromLocalStorage = localStorage.getItem(LocalStorage.GroupFilters);

    if (groupFiltersFromLocalStorage) {
      const groupFilters = JSON.parse(groupFiltersFromLocalStorage);
      const filtersForGroup = groupFilters.find((groupFilter: GroupFilter) => groupFilter.groupId === groupId);
      if (filtersForGroup) {
        yield put(fetchGroupFilterSuccess(groupFilters));
      } else {
        yield put(fetchGroupFilterSuccess([...groupFilters, { groupId, tableFilters: defaultTableFilters }]));
      }
    } else {
      yield put(fetchGroupFilterSuccess([{ groupId, tableFilters: defaultTableFilters }]));
    }
  } catch (error) {
    yield call(toast.error, `Failed to load table filters`);
  }
}

function* setGroupFilters(): Generator {
  try {
    const { groupId, groupFilters, tableFilterType, filterType, selectedFilterValues } = (yield select(
      getGroupFilterParts,
    )) as {
      groupId: string | undefined;
      groupFilters: GroupFilter[];
      tableFilterType: TableFilterType;
      filterType: string;
      selectedFilterValues: SelectedFilterValues[];
    };

    const selectedValues = selectedFilterValues.some((value) => value.key === 'all') ? [] : selectedFilterValues;

    if (groupId) {
      let newGroupFilters: GroupFilter[] = [];
      const existingGroupFilter = groupFilters.find((groupFilter) => groupFilter.groupId === groupId);
      if (existingGroupFilter) {
        // There are existing filters for this group
        const existingTableFilters = existingGroupFilter.tableFilters.find(
          (tableFilter) => tableFilter.tableFilterType === tableFilterType,
        );
        if (existingTableFilters) {
          // There are existing table filters for this group
          newGroupFilters = [
            ...groupFilters.filter((groupFilter) => groupFilter.groupId !== groupId),
            {
              groupId,
              tableFilters: [
                ...existingGroupFilter.tableFilters.filter(
                  (tableFilter) => tableFilter.tableFilterType !== tableFilterType,
                ),
                {
                  tableFilterType,
                  filters: [
                    ...existingTableFilters.filters.filter((filter) => filter.filterType !== filterType),
                    ...(selectedValues.length > 0 ? [{ filterType, selectedFilterValues: selectedValues }] : []),
                  ],
                },
              ],
            },
          ];
        } else {
          // There are no existing table filters for the group
          newGroupFilters = [
            ...groupFilters.filter((groupFilter) => groupFilter.groupId !== groupId),
            {
              groupId,
              tableFilters: [
                ...existingGroupFilter.tableFilters.filter(
                  (tableFilter) => tableFilter.tableFilterType !== tableFilterType,
                ),
                {
                  tableFilterType,
                  filters: [
                    ...(selectedValues.length > 0 ? [{ filterType, selectedFilterValues: selectedValues }] : []),
                  ],
                },
              ],
            },
          ];
        }
      } else {
        // There are no existing filters for this group, add one
        newGroupFilters = [
          ...groupFilters,
          {
            groupId,
            tableFilters: [
              {
                tableFilterType,
                filters: [...(selectedValues.length > 0 ? [{ filterType, selectedFilterValues: selectedValues }] : [])],
              },
            ],
          },
        ];
      }
      localStorage.setItem(LocalStorage.GroupFilters, JSON.stringify(newGroupFilters));

      yield put(setGroupFilterSuccess(newGroupFilters));
    }
  } catch (error) {
    yield call(toast.error, `Failed to save table filters`);
  }
}

function* clearFilters(): Generator {
  try {
    const { groupId } = (yield select(({ currentUser: { group } }: AppState) => group)) as Group;
    const { groupFilters, tableFilterType } = (yield select(({ viewer }: AppState) => viewer)) as ViewerState;

    if (groupId) {
      let newGroupFilters: GroupFilter[] = [];
      const existingGroupFilter = groupFilters.find((groupFilter) => groupFilter.groupId === groupId);
      if (existingGroupFilter) {
        // There are existing filters for this group
        const existingTableFilters = existingGroupFilter.tableFilters.find(
          (tableFilter) => tableFilter.tableFilterType === tableFilterType,
        );
        if (existingTableFilters) {
          // There are existing table filters for this group
          newGroupFilters = [
            ...groupFilters.filter((groupFilter) => groupFilter.groupId !== groupId),
            {
              groupId,
              tableFilters: [
                ...existingGroupFilter.tableFilters.filter(
                  (tableFilter) => tableFilter.tableFilterType !== tableFilterType,
                ),
                {
                  tableFilterType,
                  filters: [],
                },
              ],
            },
          ];
        } else {
          // There are no existing table filters for the group
          newGroupFilters = [
            ...groupFilters.filter((groupFilter) => groupFilter.groupId !== groupId),
            {
              groupId,
              tableFilters: [
                ...existingGroupFilter.tableFilters.filter(
                  (tableFilter) => tableFilter.tableFilterType !== tableFilterType,
                ),
                {
                  tableFilterType,
                  filters: [],
                },
              ],
            },
          ];
        }
      } else {
        // There are no existing filters for this group, add one
        newGroupFilters = [...groupFilters, { groupId, tableFilters: [{ tableFilterType, filters: [] }] }];
      }
      localStorage.setItem(LocalStorage.GroupFilters, JSON.stringify(newGroupFilters));

      yield put(clearFiltersSuccess(newGroupFilters));
    }
  } catch (error) {
    yield call(toast.error, `Failed to clear table filters`);
  }
}

function* getTheme(): Generator {
  try {
    const { group = {} } = (yield select(({ currentUser }: AppState) => currentUser)) as CurrentUserState;
    const { selectedClientId = '', defaultClientId = '' } = (yield select(
      ({ viewer }: AppState) => viewer,
    )) as ViewerState;
    const { configurators = [] } = group as Group;
    const config = configurators.find((configurator: Configurator) => configurator.clientId === selectedClientId);
    const defaultConfig = configurators.find((configurator: Configurator) => configurator.clientId === defaultClientId);

    let configTheme;
    if (selectedClientId && config) {
      configTheme = extractThemeFromConfig(config);
    } else if (!isIdeaRoomGroup((group as Group).groupId) && defaultClientId && defaultConfig) {
      configTheme = extractThemeFromConfig(defaultConfig);
    } else {
      configTheme = {};
    }
    yield put(setTheme(configTheme));
    yield put(getThemeComplete());
  } catch (error) {
    const { message = 'Failed to update Theme' } = error as Error;
    yield call(toast.error, message);
  }
}

function* getVisibleLanguages({ payload: { vendorLanguages } }: any): Generator {
  try {
    const ideaRoomUser = yield select(({ currentUser: { user = unknownUser } }: AppState) => isIdeaRoomUser(user));

    const languages = (
      (yield Promise.all([
        ...supportedLanguages.map(
          async (lng: Language): Promise<Language> =>
            ({
              ...lng,
              hidden: !ideaRoomUser && !vendorLanguages.includes(lng.key),
              order: vendorLanguages.includes(lng.key) ? vendorLanguages.indexOf(lng.key) : Infinity,
            } as Language),
        ),
      ])) as Language[]
    ).sort(({ order: a = Infinity }, { order: b = Infinity }) => a - b);

    if (ideaRoomUser) languages.push(cimode);

    // Change language if default language is different or the current language is not supported
    const defaultLanguage = languages.filter((language) => !language.hidden)[0].key;
    if (defaultLanguage !== i18n.language) {
      i18n.changeLanguage(defaultLanguage || 'en');
    }

    yield put(setVisibleLanguages(languages));
  } catch (error) {
    const { message = 'Failed to get visible languages' } = error as Error;
    yield call(toast.error, message);
  }
}

function* watchSetToast(): Generator {
  yield takeLatest(ViewerActionTypes.SetToastMessage, setToastMessage);
}

function* watchFetchVisibleColumns(): Generator {
  yield takeLatest(ViewerActionTypes.FetchVisibleColumns, fetchVisibleColumns);
}

function* watchSetVisibleColumns(): Generator {
  yield takeLatest(ViewerActionTypes.SetVisibleColumns, setVisibleColumns);
}

function* watchFetchGroupFilters(): Generator {
  yield takeLatest(ViewerActionTypes.FetchGroupFilters, fetchGroupFilters);
}

function* watchSetGroupFilters(): Generator {
  yield takeLatest(ViewerActionTypes.SetGroupFilters, setGroupFilters);
}

function* watchClearFilters(): Generator {
  yield takeLatest(ViewerActionTypes.ClearFilters, clearFilters);
}

function* watchGetTheme(): Generator {
  yield takeLatest(ViewerActionTypes.GetTheme, getTheme);
}

function* watchSetSelectedClientId(): Generator {
  yield takeLatest(ViewerActionTypes.SetSelectedClientId, getTheme);
}

function* watchResetClientIds(): Generator {
  yield takeLatest(ViewerActionTypes.ResetClientIds, getTheme);
}

function* watchGetVisibleLanguages(): Generator {
  yield takeLatest(ViewerActionTypes.GetVisibleLanguages, getVisibleLanguages);
}

export function* viewerSaga(): Generator {
  yield all([
    watchSetToast(),
    watchFetchVisibleColumns(),
    watchSetVisibleColumns(),
    watchFetchGroupFilters(),
    watchSetGroupFilters(),
    watchClearFilters(),
    watchGetTheme(),
    watchSetSelectedClientId(),
    watchResetClientIds(),
    watchGetVisibleLanguages(),
  ]);
}
